import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { Trophy, X, Percent, Zap, Crown, Lightbulb, Users } from 'lucide-react';

import s from './CompetitionPanel.css';

// GraphQL Query
const GET_COMPETITIVE_POSITION = gql`
  query getCompetitivePosition($featuredListingId: Int!) {
    getCompetitivePosition(featuredListingId: $featuredListingId) {
      currentPosition
      totalCompetitors
      myCommission
      rateToNextPosition
      rateToTop
      rateToTop5
      percentile
      nearbyCompetitors {
        position
        commissionRate
        listingTitle
        tier
      }
      suggestion
      urgency
    }
  }
`;

class CompetitionPanel extends Component {
  static propTypes = {
    featuredListingId: PropTypes.number.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onTopUp: PropTypes.func
  };

  static defaultProps = {
    isOpen: false
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      data: null
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.loadData();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.loadData();
    }
  }

  loadData = async () => {
    const { client, featuredListingId } = this.props;

    this.setState({ loading: true });

    try {
      const result = await client.query({
        query: GET_COMPETITIVE_POSITION,
        variables: { featuredListingId },
        fetchPolicy: 'network-only'
      });

      if (result?.data?.getCompetitivePosition) {
        this.setState({
          data: result.data.getCompetitivePosition,
          loading: false
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error loading competition data:', error);
      this.setState({ loading: false });
    }
  };

  getUrgencyClass = (urgency) => {
    switch (urgency) {
      case 'high': return s.urgencyHigh;
      case 'medium': return s.urgencyMedium;
      default: return s.urgencyLow;
    }
  };

  getPositionIcon = (position) => {
    if (position === 1) return <Trophy size={16} color="#F59E0B" />;
    if (position === 2) return <Trophy size={16} color="#9CA3AF" />;
    if (position === 3) return <Trophy size={16} color="#CD7F32" />;
    return `#${position}`;
  };

  render() {
    const { isOpen, onClose, onTopUp, listTitle } = this.props;
    const { loading, data } = this.state;

    if (!isOpen) return null;

    return (
      <div className={s.overlay} onClick={onClose}>
        <div className={s.panel} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={s.header}>
            <div>
              <h2 className={s.title}><Trophy size={24} /> Competencia</h2>
              {listTitle && <p className={s.subtitle}>{listTitle}</p>}
            </div>
            <button className={s.closeBtn} onClick={onClose}><X size={20} /></button>
          </div>

          {loading ? (
            <div className={s.loading}>
              <div className={s.spinner}></div>
              <p>Analizando competencia...</p>
            </div>
          ) : !data ? (
            <div className={s.noData}>
              <p>No se pudo cargar la información de competencia</p>
            </div>
          ) : (
            <>
              {/* Position Card */}
              <div className={s.positionCard}>
                <div className={s.positionMain}>
                  <span className={s.positionNumber}>
                    {this.getPositionIcon(data.currentPosition)}
                  </span>
                  <span className={s.positionLabel}>
                    de {data.totalCompetitors} competidores
                  </span>
                </div>
                <div className={s.percentileBar}>
                  <div 
                    className={s.percentileFill} 
                    style={{ width: `${data.percentile}%` }}
                  />
                </div>
                <p className={s.percentileText}>
                  Mejor que el {data.percentile}% de los competidores
                </p>
              </div>

              {/* My Commission */}
              <div className={s.bidCard}>
                <div className={s.bidInfo}>
                  <span className={s.bidLabel}><Percent size={16} /> Tu Comisión Actual</span>
                  <span className={s.bidValue}>{data.myCommission?.toFixed(1) || 0}%</span>
                </div>
              </div>

              {/* Suggestion */}
              <div className={cx(s.suggestionCard, this.getUrgencyClass(data.urgency))}>
                <p className={s.suggestionText}>{data.suggestion}</p>
              </div>

              {/* Quick Actions */}
              {data.currentPosition > 1 && (
                <div className={s.actionsSection}>
                  <h4 className={s.actionsTitle}><Zap size={18} /> Acciones Rápidas</h4>
                  <div className={s.actionButtons}>
                    {data.rateToNextPosition > 0 && (
                      <button 
                        className={s.actionBtn}
                        onClick={() => onTopUp && onTopUp(data.rateToNextPosition)}
                      >
                        <span className={s.actionAmount}>+{data.rateToNextPosition.toFixed(0)}%</span>
                        <span className={s.actionDesc}>Subir 1 posición</span>
                      </button>
                    )}
                    {data.rateToTop5 > 0 && data.currentPosition > 5 && (
                      <button 
                        className={cx(s.actionBtn, s.actionBtnPrimary)}
                        onClick={() => onTopUp && onTopUp(data.rateToTop5)}
                      >
                        <span className={s.actionAmount}>+{data.rateToTop5.toFixed(0)}%</span>
                        <span className={s.actionDesc}>Entrar al Top 5</span>
                      </button>
                    )}
                    {data.rateToTop > 0 && data.currentPosition > 1 && (
                      <button 
                        className={cx(s.actionBtn, s.actionBtnGold)}
                        onClick={() => onTopUp && onTopUp(data.rateToTop)}
                      >
                        <span className={s.actionAmount}>+{data.rateToTop.toFixed(0)}%</span>
                        <span className={s.actionDesc}>Ser #1</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Nearby Competitors */}
              {data.nearbyCompetitors && data.nearbyCompetitors.length > 0 && (
                <div className={s.competitorsSection}>
                  <h4 className={s.competitorsTitle}><Users size={18} /> Competidores Cercanos</h4>
                  <div className={s.competitorsList}>
                    {data.nearbyCompetitors.map((comp, i) => (
                      <div 
                        key={i} 
                        className={cx(s.competitorRow, {
                          [s.competitorAbove]: comp.position < data.currentPosition,
                          [s.competitorBelow]: comp.position > data.currentPosition
                        })}
                      >
                        <span className={s.competitorPosition}>
                          {this.getPositionIcon(comp.position)}
                        </span>
                        <span className={s.competitorInfo}>
                          {comp.listingTitle || 'Sin título'}
                          {comp.tier === 'premium' && <span className={s.premiumBadge}><Crown size={14} /></span>}
                        </span>
                        <span className={s.competitorBid}>
                          {comp.commissionRate?.toFixed(1) || 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className={s.tipsSection}>
                <h4 className={s.tipsTitle}><Lightbulb size={18} /> Cómo mejorar tu posición</h4>
                <ul className={s.tipsList}>
                  <li>Aumenta tu % de comisión para subir en el ranking</li>
                  <li>Un bote Premium tiene badge dorado destacado</li>
                  <li>Mejores posiciones = más visibilidad = más reservas</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(s),
  withApollo
)(CompetitionPanel);
